import { type FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Button } from 'components/Button/Button'
import { useModalContext } from 'context/modalContext'
import { type ICreatorPriceLimits } from 'types/interfaces/IProfile'

import './_priceModal.scss'


const toNumber = (v: unknown, fallback = 0) => {
  if (v === null || v === undefined) return fallback
  if (typeof v === 'number') return Number.isFinite(v) ? v : fallback
  if (typeof v === 'string') {
    const n = parseFloat(v)
    return Number.isFinite(n) ? n : fallback
  }
  return fallback
}

const PriceModal: FC<{
  updatePrice: (val: number) => void
  postLimit?: number
  priceLimits?: ICreatorPriceLimits | null
}> = ({ updatePrice, priceLimits }) => {
  const [price, setPrice] = useState<number | null>(null)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const modalData = useModalContext()
  const { t } = useTranslation()

  const { min, max, ready } = useMemo(() => {
    const min = toNumber(priceLimits?.min_premium_video_price, 0)
    const max = toNumber(priceLimits?.max_premium_video_price, 0)
    const ready = !!priceLimits && (min > 0 || max > 0)
    return { min, max, ready }
  }, [priceLimits])

  const isLoading = !ready


  const validate = (numericValue: number | null) => {
    if (!ready || numericValue === null) {
      setHasError(false)
      setErrorMessage('')
      return
    }

    if (numericValue < min) {
      setHasError(true)
      setErrorMessage(`${t('minPriceIs')} $${min}`)
      return
    }

    if (max > 0 && numericValue > max) {
      setHasError(true)
      setErrorMessage(`${t('maxPriceIs')} $${max}`)
      return
    }

    setHasError(false)
    setErrorMessage('')
  }

  const handleChange = (value: string) => {
    if (/^\d*\.?\d{0,2}$/.test(value) || value === '') {
      const numericValue = value === '' ? null : parseFloat(value)
      setPrice(numericValue)
      validate(numericValue)
    }
  }

  return (
    <div className="priceModal">
      <div className="priceModal__body">
        <div className="priceModal__input">
          <input
            autoFocus
            type="number"
            value={price ?? ''}
            onChange={(e) => handleChange(e.currentTarget.value)}
            step="0.01"
            inputMode="decimal"
          />
          <div className="priceModal--dollar">$</div>
        </div>

        <div className="priceModal__statusRow">
          <div className="priceModal__info">
            {!isLoading ? (
              <p className="priceModal__minmax">
                <span className="priceModal__label">{t('minimum')}</span>{' '}
                <span className="priceModal__price">${min}</span>{' '}
                <span className="priceModal__divider">|</span>{' '}
                <span className="priceModal__label">{t('maximum')}</span>{' '}
                <span className="priceModal__price">${max}</span>
              </p>
            ) : (
              <span className="priceModal__loading">Loading...</span>
            )}
          </div>

          {hasError ? (
            <p className="priceModal__errorInline" aria-live="polite">
              {errorMessage}
            </p>
          ) : (
            <span className="priceModal__errorInlinePlaceholder" aria-hidden="true" />
          )}
        </div>

        <Button
          text={t('save')}
          color="blue"
          font="mont-14-normal"
          width="13"
          height="3"
          customClass="priceModal__button"
          disabled={isLoading || price === null || hasError}
          clickFn={() => {
            if (!ready) return

            const numericPrice = price ?? 0
            validate(numericPrice)

            if (numericPrice < min) return
            if (max > 0 && numericPrice > max) return

            updatePrice(numericPrice)
            modalData.clearModal()
          }}
        />
      </div>
    </div>
  )
}

export default PriceModal
